import { useEffect, useRef, useMemo, useState, useCallback } from "react";
import Map, {
  Source,
  Layer,
  Popup,
  NavigationControl,
  FullscreenControl,
} from "react-map-gl/mapbox";
import type { MapRef, LayerProps } from "react-map-gl/mapbox";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import "mapbox-gl/dist/mapbox-gl.css";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

interface MapPoint {
  lat: number;
  lon: number;
  bitrate: number;
  color: string;
  timestamp: number;
  interpolated: boolean;
  links: Record<string, number>;
}

interface SessionMapData {
  id: string;
  points: MapPoint[];
  visible: boolean;
  deviceName?: string;
}

interface CoverageMapViewerProps {
  sessions: SessionMapData[];
  isLoading?: boolean;
  className?: string;
}

export function CoverageMapViewer({
  sessions,
  isLoading,
  className = "",
}: CoverageMapViewerProps) {
  const mapRef = useRef<MapRef>(null);
  const [popupInfo, setPopupInfo] = useState<{
    longitude: number;
    latitude: number;
    bitrate: number;
    timestamp: number;
    links: Record<string, number>;
    sessionId: string;
    deviceName?: string;
  } | null>(null);
  const [mapStyle, setMapStyle] = useState<"streets" | "satellite">("streets");

  // Build GeoJSON for all visible sessions
  const geoJsonData = useMemo(() => {
    const features: GeoJSON.Feature[] = [];

    sessions
      .filter((s) => s.visible && s.points.length > 0)
      .forEach((session) => {
        // Create line segments with color based on bitrate
        for (let i = 0; i < session.points.length - 1; i++) {
          const p1 = session.points[i];
          const p2 = session.points[i + 1];

          features.push({
            type: "Feature",
            properties: {
              color: p1.color,
              bitrate: p1.bitrate,
              sessionId: session.id,
              deviceName: session.deviceName,
            },
            geometry: {
              type: "LineString",
              coordinates: [
                [p1.lon, p1.lat],
                [p2.lon, p2.lat],
              ],
            },
          });
        }

        // Add circle markers for each point
        session.points.forEach((point) => {
          features.push({
            type: "Feature",
            properties: {
              color: point.color,
              bitrate: point.bitrate,
              timestamp: point.timestamp,
              links: JSON.stringify(point.links),
              sessionId: session.id,
              deviceName: session.deviceName,
              isPoint: true,
            },
            geometry: {
              type: "Point",
              coordinates: [point.lon, point.lat],
            },
          });
        });
      });

    return {
      type: "FeatureCollection" as const,
      features,
    };
  }, [sessions]);

  // Calculate bounds for visible sessions
  const bounds = useMemo(() => {
    const visiblePoints = sessions
      .filter((s) => s.visible)
      .flatMap((s) => s.points);

    if (visiblePoints.length === 0) return null;

    const lats = visiblePoints.map((p) => p.lat);
    const lons = visiblePoints.map((p) => p.lon);

    return {
      minLon: Math.min(...lons),
      maxLon: Math.max(...lons),
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats),
    };
  }, [sessions]);

  // Fit bounds when data changes
  const fitToBounds = useCallback(() => {
    if (bounds && mapRef.current) {
      mapRef.current.fitBounds(
        [
          [bounds.minLon, bounds.minLat],
          [bounds.maxLon, bounds.maxLat],
        ],
        { padding: 50, duration: 500 },
      );
    }
  }, [bounds]);

  useEffect(() => {
    fitToBounds();
  }, [fitToBounds]);

  const lineLayer: LayerProps = {
    id: "coverage-lines",
    type: "line",
    filter: ["!", ["has", "isPoint"]],
    paint: {
      "line-color": ["get", "color"],
      "line-width": 4,
      "line-opacity": 0.8,
    },
  };

  const pointLayer: LayerProps = {
    id: "coverage-points",
    type: "circle",
    filter: ["has", "isPoint"],
    paint: {
      "circle-radius": 5,
      "circle-color": ["get", "color"],
      "circle-stroke-width": 1,
      "circle-stroke-color": "#fff",
    },
  };

  const handleMapClick = (event: mapboxgl.MapLayerMouseEvent) => {
    const feature = event.features?.[0];
    if (feature && feature.properties?.isPoint) {
      const coords = (feature.geometry as GeoJSON.Point).coordinates;
      setPopupInfo({
        longitude: coords[0],
        latitude: coords[1],
        bitrate: feature.properties.bitrate,
        timestamp: feature.properties.timestamp,
        links: JSON.parse(feature.properties.links || "{}"),
        sessionId: feature.properties.sessionId,
        deviceName: feature.properties.deviceName,
      });
    }
  };

  const formatBitrate = (kbps: number) => {
    if (kbps >= 1000) {
      return `${(kbps / 1000).toFixed(1)} Mbps`;
    }
    return `${kbps} kbps`;
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  if (!MAPBOX_TOKEN) {
    return (
      <div
        className={`flex items-center justify-center bg-muted rounded-lg ${className}`}
      >
        <p className="text-muted-foreground">
          Mapbox token not configured. Set VITE_MAPBOX_TOKEN in your
          environment.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className={`flex items-center justify-center bg-muted rounded-lg ${className}`}
      >
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasVisibleData = sessions.some((s) => s.visible && s.points.length > 0);

  return (
    <div className={`relative rounded-lg overflow-hidden ${className}`}>
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{
          longitude: 8.5417,
          latitude: 47.3769,
          zoom: 10,
        }}
        style={{ width: "100%", height: "100%" }}
        mapStyle={
          mapStyle === "streets"
            ? "mapbox://styles/mapbox/dark-v11"
            : "mapbox://styles/mapbox/satellite-streets-v12"
        }
        interactiveLayerIds={["coverage-points"]}
        onClick={handleMapClick}
        cursor="pointer"
      >
        <NavigationControl position="top-right" />
        <FullscreenControl position="top-right" />

        {hasVisibleData && (
          <Source id="coverage-data" type="geojson" data={geoJsonData}>
            <Layer {...lineLayer} />
            <Layer {...pointLayer} />
          </Source>
        )}

        {popupInfo && (
          <Popup
            longitude={popupInfo.longitude}
            latitude={popupInfo.latitude}
            anchor="bottom"
            onClose={() => setPopupInfo(null)}
            closeButton={true}
            closeOnClick={false}
          >
            <div className="p-2 text-sm">
              {popupInfo.deviceName && (
                <p className="font-medium">{popupInfo.deviceName}</p>
              )}
              <p className="font-medium">
                Total: {formatBitrate(popupInfo.bitrate)}
              </p>
              <p className="text-muted-foreground text-xs">
                {formatTime(popupInfo.timestamp)}
              </p>
              {Object.keys(popupInfo.links).length > 0 && (
                <div className="mt-2 border-t pt-2">
                  <p className="text-xs font-medium mb-1">Per operator:</p>
                  {Object.entries(popupInfo.links).map(([name, kbps]) => (
                    <p key={name} className="text-xs">
                      {name}: {formatBitrate(kbps as number)}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </Popup>
        )}
      </Map>

      {/* Map controls overlay */}
      <div className="absolute bottom-4 left-4 flex gap-2">
        <Button
          size="sm"
          variant={mapStyle === "streets" ? "default" : "outline"}
          onClick={() => setMapStyle("streets")}
        >
          Streets
        </Button>
        <Button
          size="sm"
          variant={mapStyle === "satellite" ? "default" : "outline"}
          onClick={() => setMapStyle("satellite")}
        >
          Satellite
        </Button>
        {hasVisibleData && (
          <Button size="sm" variant="outline" onClick={fitToBounds}>
            Fit to tracks
          </Button>
        )}
      </div>

      {/* Legend */}
      <div className="absolute top-4 left-4 bg-background/90 backdrop-blur-sm rounded-lg p-3 text-xs">
        <p className="font-medium mb-2">Signal Quality</p>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span>Offline (0 kbps)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-orange-500" />
            <span>Poor (&lt;1 Mbps)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span>Fair (1-3 Mbps)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span>Good (&gt;3 Mbps)</span>
          </div>
        </div>
      </div>

      {!hasVisibleData && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/50">
          <p className="text-muted-foreground">
            No coverage data to display. Enable session visibility to see
            tracks.
          </p>
        </div>
      )}
    </div>
  );
}
